import React, { useState } from "react";
import { useTranslation } from "react-i18next";

export default function ApparentPowerReportDialog({
  open,
  onClose,
  deviceId,
  deviceType,
  API_BASE,
  isVirtual = false,
}) {
  const { t } = useTranslation();
  const [format, setFormat] = useState("csv");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    try {
      setError("");
      if (!startDate || !endDate) {
        setError(t('reports.dateError'));
        return;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      if (end <= start) {
        setError(t('reports.endDateError'));
        return;
      }

      setLoading(true);

      const params = new URLSearchParams({
        ...(isVirtual ? { virtualId: deviceId } : { deviceId }),
        deviceType,
        start: start.toISOString(),
        end: end.toISOString(),
        format,
        lang: localStorage.getItem("i18nextLng") || "tr",
      });
      const endpoint = isVirtual
        ? "/virtual-reports/export-apparent-power"
        : "/reports/export-apparent-power";

      const url = `${API_BASE}${endpoint}?${params.toString()}`;

      if (format === "png") {
        const res = await fetch(url);
        if (!res.ok) throw new Error(t('reports.downloadError'));
        const blob = await res.blob();
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `apparent_power_${Date.now()}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
      } else {
        window.open(url, "_blank");
      }

      onClose();
    } catch (err) {
      console.error(t('reports.downloadError') + ":", err);
      setError(t('reports.creationError'));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">
          {t('apparentPower.powerTypeApparent')}
        </h2>

        <label className="block text-sm font-medium mb-1">{t('reports.format')}</label>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className="w-full border rounded-md p-2 mb-4"
        >
          <option value="csv">CSV</option>
          <option value="xlsx">{t('reports.excel')}</option>
          <option value="png">{t('reports.chart')} (PNG)</option>
        </select>

        <label className="block text-sm font-medium mb-1">
          {t('reports.startDate')}
        </label>
        <input
          type="datetime-local"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
          }}
          className="w-full border rounded-md p-2 mb-3"
        />

        <label className="block text-sm font-medium mb-1">{t('reports.endDate')}</label>
        <input
          type="datetime-local"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
          className="w-full border rounded-md p-2 mb-4"
        />

        {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

        {loading ? (
          <div className="flex justify-center py-3">
            <div className="animate-spin h-6 w-6 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-md bg-gray-300 hover:bg-gray-400 text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleDownload}
              className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
            >
              {t('common.download')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
